<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import { ArrowLeft, Send, Plane } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  conversation: { type: Object, required: true },
  messages: { type: Array, required: true },
})

const form = useForm({ text: '' })
const podeEnviar = computed(() => form.text.trim().length > 0 && !form.processing)
const fim = ref(null)

const responder = () => {
  if (!podeEnviar.value) return
  form.post(`/admin/mensagens/${props.conversation.id}/respostas`, {
    preserveScroll: true,
    onSuccess: () => form.reset('text'),
  })
}

// Conversa longa: manter o fim à vista depois de enviar.
watch(() => props.messages.length, () => nextTick(() => fim.value?.scrollIntoView({ block: 'end' })))

const iniciais = computed(
  () =>
    props.conversation.client
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('') || '—',
)
</script>

<template>
  <Head :title="`${props.conversation.client} · Mensagens`" />

  <Link href="/admin/mensagens" class="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-up-navy">
    <ArrowLeft class="h-4 w-4" aria-hidden="true" />
    Mensagens
  </Link>

  <div class="mt-3 flex flex-wrap items-baseline gap-x-3">
    <h1 class="text-xl font-semibold tracking-tight text-up-navy">{{ props.conversation.client }}</h1>
    <Link :href="`/admin/aeronaves/${props.conversation.aircraftId}`" class="flex items-center gap-1 text-sm text-slate-500 hover:text-up-navy">
      <Plane class="h-4 w-4" aria-hidden="true" />
      {{ props.conversation.aircraft }} · {{ props.conversation.serviceOrder }}
    </Link>
  </div>

  <div class="mt-6 rounded-card bg-white p-6 shadow-card">
    <p v-if="!props.messages.length" class="text-sm text-slate-500">
      Nenhuma mensagem ainda. A resposta abaixo começa a conversa.
    </p>

    <!--
      Espelha o lado do cliente: equipe à esquerda, cliente à direita. Aqui os
      papéis não se invertem — quem lê é a equipe, mas o desenho é o mesmo para
      a conversa ser reconhecível.
    -->
    <ol v-else class="space-y-4" aria-live="polite">
      <li
        v-for="m in props.messages"
        :key="m.id"
        class="flex gap-3"
        :class="m.from === 'client' && 'flex-row-reverse'"
      >
        <span
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
          :class="m.from === 'team' ? 'bg-up-navy text-white' : 'bg-up-orange text-white'"
          aria-hidden="true"
        >{{ m.from === 'team' ? 'UP' : iniciais }}</span>

        <div class="max-w-[80%]">
          <p class="flex flex-wrap items-baseline gap-x-2" :class="m.from === 'client' && 'justify-end'">
            <span class="text-sm font-semibold text-up-navy">{{ m.author }}</span>
            <time class="text-xs text-slate-400">{{ m.at }}</time>
            <span v-if="m.subject" class="rounded bg-up-bg px-1.5 py-0.5 text-[11px] text-slate-500">{{ m.subject }}</span>
          </p>
          <p
            class="mt-1 rounded-lg px-4 py-3 text-sm leading-relaxed"
            :class="m.from === 'team' ? 'bg-up-bg text-slate-600' : 'bg-up-orange/10 text-up-navy'"
          >{{ m.text }}</p>
        </div>
      </li>
    </ol>
    <div ref="fim" />

    <form class="mt-6 border-t border-slate-100 pt-5" @submit.prevent="responder">
      <label for="resposta" class="block text-sm font-medium text-up-navy">Responder como equipe</label>
      <textarea
        id="resposta"
        v-model="form.text"
        rows="3"
        placeholder="Escreva a resposta ao cliente…"
        class="mt-2 w-full resize-y rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-up-navy outline-none focus:border-up-orange focus:ring-2 focus:ring-up-orange/30"
      />
      <p v-if="form.errors.text" role="alert" class="mt-1 text-sm text-up-orange-dark">{{ form.errors.text }}</p>

      <div class="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          :disabled="!podeEnviar"
          class="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
          :class="podeEnviar ? 'bg-up-orange text-white hover:bg-up-orange-dark' : 'cursor-not-allowed bg-slate-200 text-slate-400'"
        >
          <Send class="h-4 w-4" aria-hidden="true" />
          {{ form.processing ? 'Enviando…' : 'Enviar resposta' }}
        </button>
        <!-- O nome de quem responde vai gravado e aparece para o cliente. -->
        <p class="text-xs text-slate-500">A resposta aparece no portal do cliente com o seu nome.</p>
      </div>
    </form>
  </div>
</template>
